import { useQuery } from "@tanstack/react-query";
import useAPI from "../api/hooks/useAPI";
import useMSSP from "../api/hooks/useMSSP";
import { platformUsersKeys } from "../api/queryKeys";
import { getInternationalFormattedDate } from "../utils/timestamp";

// File naming constants
export const FILE_NAME_CONSTANTS = {
  SEPARATOR: "_",
  BREEZE: "breeze",
};

// File extension constants
export const FILE_EXTENSIONS = {
  PDF: ".pdf",
  CSV: ".csv",
};

// Core utility functions (can be used directly if needed)
export function generateFileName(
  baseName,
  customerId = null,
  extension = null
) {
  const dateString = getInternationalFormattedDate();
  const customerIdPart = customerId ? `${customerId}` : "";
  const parts = [customerIdPart, baseName, dateString].filter(Boolean);
  return `${parts.join(FILE_NAME_CONSTANTS.SEPARATOR)}${extension ?? ""}`;
}

export function generatePdfFileName(baseName, customerId = null) {
  return generateFileName(baseName, customerId, FILE_EXTENSIONS.PDF);
}

export function generateCsvFileName(baseName, customerId = null) {
  return generateFileName(baseName, customerId, FILE_EXTENSIONS.CSV);
}

export function sanitizeFileName(filename) {
  // Replace invalid characters with underscores
  return filename.replace(/[<>:"/\\|?*\s]/g, "_").replace(/_{2,}/g, "_");
}

export function generateBreezeFileName(
  section,
  customerId = null,
  extension = null
) {
  const baseName = `${FILE_NAME_CONSTANTS.BREEZE}_${section}`;
  return generateFileName(baseName, customerId, extension);
}

export function useFileNaming() {
  const { getPlatformUser } = useAPI();
  const { isMSSP, currentTenantId } = useMSSP();

  const { data: platformUser } = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  // Determine the customer ID based on user type
  const getCustomerId = () => {
    if (isMSSP) {
      return getCustomerNameFromCustomerId(currentTenantId);
    }
    if (!platformUser?.customer_id) {
      return null;
    }
    return getCustomerNameFromCustomerId(platformUser.customer_id);
  };

  const getCustomerNameFromCustomerId = (customerId) => {
    if (typeof customerId !== "string" || customerId.length === 0) {
      return null;
    }
    const parts = customerId.split(".");
    if (customerId.includes("support")) {
      return parts[1] ?? null;
    }
    return parts[0] ?? null;
  };

  const customerId = getCustomerId();

  return {
    generateFileName: (baseName, extension = null) =>
      generateFileName(baseName, customerId, extension),
    generatePdfFileName: (baseName) =>
      generatePdfFileName(baseName, customerId),
    generateCsvFileName: (baseName) =>
      generateCsvFileName(baseName, customerId),
    generateBreezeFileName: (section, extension) =>
      generateBreezeFileName(section, extension, customerId),
    sanitizeFileName,
    customerId,
  };
}
